import { styled } from "styled-components";

import { LinkButton } from "@vericus/cadmus-ui";

import dayjs from "dayjs";

import { Step } from "./internal";

interface Props {
  /** Current step */
  step: Step;
  /** Final due date */
  dueDate: Date | null;
  /** Change the current step's date */
  setDate: (date: Date | null) => void;
  /** Callback to jump the visible month to today */
  jumpToToday(): void;
}
/**
 * Date jump actions for the currently rendered `<DatePicker />`.
 */
export function QuickOptions(props: Props) {
  const { step, dueDate, setDate, jumpToToday } = props;
  if (step === Step.Final) {
    return (
      <>
        <QuickOptionButton marginRight={18} onClick={jumpToToday}>
          Jump to this month
        </QuickOptionButton>
        <QuickOptionButton
          marginRight={18}
          onClick={() => setDate(dayjs().add(2, "week").toDate())}
        >
          2 weeks from today
        </QuickOptionButton>
        <QuickOptionButton
          onClick={() => setDate(dayjs().add(4, "week").toDate())}
        >
          4 weeks from today
        </QuickOptionButton>
      </>
    );
  }

  if (step === Step.Return) {
    return (
      <>
        <QuickOptionButton marginRight={18} onClick={jumpToToday}>
          Jump to this month
        </QuickOptionButton>
        <QuickOptionButton
          marginRight={18}
          onClick={() =>
            setDate(
              dayjs(dueDate ?? new Date())
                .add(1, "week")
                .toDate()
            )
          }
        >
          1 week <em>after</em> final
        </QuickOptionButton>
        <QuickOptionButton
          onClick={() => dueDate && setDate(dayjs(dueDate).add(2, "week").toDate())}
        >
          2 weeks <em>after</em> final
        </QuickOptionButton>
      </>
    );
  }

  const oneWeekBeforeFinal = dueDate && dayjs(dueDate).subtract(1, "week").toDate();
  const twoWeeksBeforeFinal =
    dueDate && dayjs(dueDate).subtract(2, "week").toDate();

  return (
    <>
      <QuickOptionButton marginRight={18} onClick={jumpToToday}>
        Jump to this month
      </QuickOptionButton>
      <QuickOptionButton
        marginRight={18}
        onClick={() => oneWeekBeforeFinal && setDate(oneWeekBeforeFinal)}
        // disabled if it's not a viable option
        disabled={
          !oneWeekBeforeFinal || !dayjs(oneWeekBeforeFinal).isAfter(dayjs())
        }
      >
        1 week <em>before</em> final
      </QuickOptionButton>
      <QuickOptionButton
        onClick={() => twoWeeksBeforeFinal && setDate(twoWeeksBeforeFinal)}
        // disabled if it's not a viable option
        disabled={
          !twoWeeksBeforeFinal || !dayjs(twoWeeksBeforeFinal).isAfter(dayjs())
        }
      >
        2 weeks <em>before</em> final
      </QuickOptionButton>
    </>
  );
}

function QuickOptionButton(props: {
  onClick: () => void;
  children: React.ReactNode;
  marginRight?: number;
  disabled?: boolean;
}) {
  return (
    <StyledLinkButton
      color="neutralgrey500"
      marginRight={props.marginRight}
      onClick={props.onClick}
      disabled={props.disabled}
      aria-disabled={props.disabled ? "true" : "false"}
    >
      {props.children}
    </StyledLinkButton>
  );
}

const StyledLinkButton = styled(LinkButton)`
  font-weight: 600;
`;
